import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerApi } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { tWork, tStatus } from "@/i18n/translations";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ArrowLeft, AlertTriangle, Clock, ChevronRight, MessageSquare, Inbox } from "lucide-react";

function fmt(d) {
  return new Date(d).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
}

export default function MyRequests() {
  const nav = useNavigate();
  const { t, lang } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    customerApi.get("/quotes/mine").then(({ data }) => setQuotes(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <button data-testid="myreq-back-btn" onClick={() => nav("/home")}
          className="flex items-center gap-2 text-primary font-medium mb-6">
          <ArrowLeft className="h-5 w-5" /> {t("back_to_home")}
        </button>
        <h2 className="font-display text-2xl font-bold text-primary">{t("my_requests")}</h2>

        {loading ? (
          <p className="text-muted-foreground mt-8">{t("loading")}</p>
        ) : quotes.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Inbox className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{t("no_requests")}</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {quotes.map((q) => {
              const urgent = q.urgency === "Urgent";
              return (
                <button key={q.id} data-testid={`myreq-card-${q.ref_number}`} onClick={() => nav(`/requests/${q.id}`)}
                  className="block w-full text-left rounded-3xl border border-border bg-white p-5 shadow-sm hover:-translate-y-1 active:scale-95 transition-transform duration-200">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${urgent ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}>
                      {urgent ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      {urgent ? t("urgent") : t("not_urgent")}
                    </span>
                    <span className="font-display text-sm font-bold text-primary">{q.ref_number}</span>
                  </div>
                  <p className="font-semibold text-foreground mt-3">{tWork(q.work_type, lang)}
                    {q.vehicle && <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{t("mobile_service_badge")}</span>}
                  </p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground">{tStatus(q.status, lang)}</span>
                    {q.unread_messages > 0 && (
                      <span data-testid={`myreq-unread-${q.ref_number}`} className="flex items-center gap-1 rounded-full bg-destructive px-2.5 py-1 text-xs font-bold text-destructive-foreground">
                        <MessageSquare className="h-3 w-3" /> {q.unread_messages}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground space-y-0.5">
                    <p>{t("submitted_on")}: {fmt(q.created_at)}</p>
                    <p>{t("last_updated")}: {fmt(q.updated_at)}</p>
                    {q.appointment?.date && (
                      <p className="text-primary font-medium">{t("appointment")}: {q.appointment.date} · {q.appointment.state}</p>
                    )}
                  </div>
                  <div className="mt-2 flex justify-end"><ChevronRight className="h-5 w-5 text-primary/50" /></div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
